import { onMounted, onBeforeUnmount } from 'vue';
import type { Ref, ShallowRef } from 'vue';

import type OLMap from 'ol/Map';
import type TileLayer from 'ol/layer/Tile';
import type OSM from 'ol/source/OSM';
import type VectorLayer from 'ol/layer/Vector';
import type VectorSource from 'ol/source/Vector';
import type MapBrowserEvent from 'ol/MapBrowserEvent';

import { CCM_MODE } from '@/components/map/cnetConstructorMode/ccmConsts';
import { initMap } from '@/components/map/composables/mapOperations';


type DebouncedUpdateSource = {
	(doFit: boolean): void;
	cancel: () => void;
};


type UseMapLifecycleParams = {
	mapRoot: Ref<HTMLElement | null | undefined>;
	maxZoom: number;
	updateBSData: () => void;
	olMap: ShallowRef<OLMap | null>;
	vectorLayer: ShallowRef<VectorLayer<VectorSource> | null>;
	tileLayer: ShallowRef<TileLayer<OSM> | null>;
	sourceCCM: Ref<VectorSource | null>;
	measureSource: Ref<VectorSource | null>;
	debouncedUpdateSource: DebouncedUpdateSource;
	onClick: (event: MapBrowserEvent<UIEvent>) => void;
	updateGnssFeatures: () => void;
	onHover: (event: MapBrowserEvent<UIEvent>) => void;
	onZoom: () => void;
	onMoveEnd: () => void;
	ccmEnabled: Ref<boolean>;
	ccmDrawMode: Ref<number>;
	addInteractions: () => void;
};


export function useMapLifecycle(params: UseMapLifecycleParams): void {
	const {
		mapRoot,
		maxZoom,
		updateBSData,
		olMap,
		vectorLayer,
		tileLayer,
		sourceCCM,
		measureSource,
		debouncedUpdateSource,
		onClick,
		updateGnssFeatures,
		onHover,
		onZoom,
		onMoveEnd,
		ccmEnabled,
		ccmDrawMode,
		addInteractions,
	} = params;

	onMounted(() => {
		updateBSData();
		const root = mapRoot.value;
		if (root == null) {
			return;
		}
		const mapElements = initMap(root, maxZoom);
		olMap.value = mapElements.olMap;
		vectorLayer.value = mapElements.vectorLayer;
		sourceCCM.value = mapElements.sourceCCM;
		tileLayer.value = mapElements.tileLayer;
		measureSource.value = mapElements.measureSource;
		debouncedUpdateSource(true);
		const mapInstance = olMap.value;
		if (mapInstance == null) {
			return;
		}
		mapInstance.on('click', onClick);
		mapInstance.on('postrender', updateGnssFeatures);
		mapInstance.on('pointermove', onHover);
		mapInstance.on('moveend', onZoom);
		mapInstance.on('moveend', onMoveEnd);
		if (ccmEnabled.value && ccmDrawMode.value === CCM_MODE.drawRectangles) {
			addInteractions();
		}
	});

	onBeforeUnmount(() => {
		debouncedUpdateSource.cancel();
		const mapInstance = olMap.value;
		if (mapInstance == null) {
			return;
		}
		mapInstance.un('click', onClick);
		mapInstance.un('postrender', updateGnssFeatures);
		mapInstance.un('pointermove', onHover);
		mapInstance.un('moveend', onZoom);
		mapInstance.un('moveend', onMoveEnd);
	});
}
